"use client";

import { fromDate, toCalendarDate } from "@internationalized/date";
import { useTranslations } from "next-intl";
import { useQueryStates } from "nuqs";
import { useMemo, useState, useTransition, type ReactNode } from "react";
import { useDebounceCallback } from "usehooks-ts";

import type { HomePageData } from "~/app/[locale]/(default)/_lib/get-home-page";
import { H2 } from "~/components/typography";
import { DatePicker } from "~/components/ui/date-picker";
import { Field, Label } from "~/components/ui/fieldset";
import { Input } from "~/components/ui/input";

import { tourSearchParams } from "../_lib/search-params";
import { MultiLocationPicker } from "./multi-location-picker";

export function SearchFilters({
  locations,
  children,
}: {
  locations: HomePageData["allLocations"];
  children: ReactNode;
}) {
  const t = useTranslations("Tours");
  const tCommon = useTranslations("Common");
  const [isLoading, startTransition] = useTransition();

  const [searchParams, setSearchParams] = useQueryStates(tourSearchParams, {
    startTransition,
    shallow: false,
    throttleMs: 400,
  });

  const [query, setQuery] = useState(searchParams.query);
  const callback = useMemo(() => {
    return (query: string) => {
      void setSearchParams({ query });
    };
  }, [setSearchParams]);
  const debouncedQuery = useDebounceCallback(callback, 800);

  return (
    <div className="flex flex-col gap-4 md:grid md:grid-cols-[300px_1fr] md:gap-8">
      <div className="flex flex-col items-stretch justify-start gap-4">
        <H2>{tCommon("tours")}</H2>
        <div>
          <Field>
            <Label>{isLoading ? t("searching") : tCommon("search")}</Label>
            <Input
              data-slot="control"
              placeholder={t("searchPlaceholder")}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                debouncedQuery(event.target.value);
              }}
            />
          </Field>
        </div>
        <div className="relative">
          <div className="absolute right-0 top-px">
            <button
              type="button"
              className="text-prune-500 text-xs font-medium"
              onClick={() => {
                void setSearchParams({ date: null });
              }}
            >
              {t("clear")}
            </button>
          </div>
          <Field>
            <Label>{t("date")}</Label>
            <DatePicker
              value={
                searchParams.date
                  ? toCalendarDate(fromDate(searchParams.date, "UTC"))
                  : null
              }
              onChange={(value) => {
                void setSearchParams({
                  date: value.toDate("UTC"),
                });
              }}
            />
          </Field>
        </div>
        <div className="relative">
          <div className="absolute right-0 top-px">
            <button
              type="button"
              className="text-prune-500 text-xs font-medium"
              onClick={() => {
                void setSearchParams({ locations: null });
              }}
            >
              {t("clear")}
            </button>
          </div>
          <Field>
            <Label>{t("locations")}</Label>
            <MultiLocationPicker
              locations={locations}
              onChange={(value) => {
                void setSearchParams({ locations: value });
              }}
              value={searchParams.locations}
            />
          </Field>
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}
